import { z } from 'zod'
import { BboxSchema, OsmTypeSchema, PlaceAreaSchema } from '../domain/geo'
import { CountryIso3Schema } from '../primitives'

/**
 * Geocoding proxy for the residence-area editor (PLAN v4 §9 + R15). Organizer-authenticated; the server queries the
 * public Nominatim instance (https://operations.osmfoundation.org/policies/nominatim/): searches ONLY on an explicit
 * request (Enter / button, never typeahead), 1 request/s for the whole application, identifying User-Agent, 7-day
 * cache. Member data is never sent. Show "© OpenStreetMap contributors" wherever a boundary is displayed.
 */

export const OSM_ATTRIBUTION = '© OpenStreetMap contributors'
export const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright'

/* GET /api/org/geo/search?q=&lang= → GeoSearchResponse (≤8 candidates, no polygons). */
export const GeoSearchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Type at least 2 characters').max(200),
  /** Accept-Language for the names (e.g. `es`, `en`); default `en`. */
  lang: z
    .string()
    .trim()
    .regex(/^[a-zA-Z]{2,3}(-[a-zA-Z]{2})?$/)
    .optional(),
})
export type GeoSearchQuery = z.infer<typeof GeoSearchQuerySchema>

export const GeoCandidateSchema = z.object({
  osmType: OsmTypeSchema,
  osmId: z.number().int().positive(),
  /** Nominatim display_name ("Barcelona, Barcelonès, Barcelona, Catalunya, España"). */
  displayName: z.string(),
  /** Short name (`name`), falls back to the first part of displayName. */
  name: z.string(),
  /** Nominatim `addresstype` (city, state, country, …): shown so the organizer picks the right level. */
  placeType: z.string(),
  placeRank: z.number().int().nullable(),
  /** OSM admin_level (extratags), null when not an administrative boundary. */
  adminLevel: z.number().int().nullable(),
  countryIso3: CountryIso3Schema.nullable(),
  lat: z.number(),
  lng: z.number(),
  bbox: BboxSchema.nullable(),
  /** Relations / ways can have a boundary; nodes cannot (offer a circle instead). */
  hasBoundary: z.boolean(),
})
export type GeoCandidate = z.infer<typeof GeoCandidateSchema>

export const GeoSearchResponseSchema = z.object({
  candidates: z.array(GeoCandidateSchema),
  attribution: z.string(),
  /** Served from the server cache (no Nominatim request was made). */
  cached: z.boolean(),
})
export type GeoSearchResponse = z.infer<typeof GeoSearchResponseSchema>

/*
 * GET /api/org/geo/boundary?osm=R347950&rank=16&lang= → GeoBoundaryResponse: the place as a storable `place` area
 * (geometry simplified to ≤5 000 points / ≤100 KB, bbox, countryIso3 for countries). 404 not_found (unknown id);
 * 422 geo_no_boundary (a point-only place: use a circle); 503 geocoder_unavailable.
 */
export const GeoBoundaryQuerySchema = z.object({
  /** `N|W|R` + OSM id, e.g. `R349053`. */
  osm: z.string().regex(/^[NWR]\d{1,15}$/, 'Expected an OSM id like R349053'),
  /** Candidate placeRank (picks the server-side simplification threshold); optional. */
  rank: z.coerce.number().int().min(0).max(30).optional(),
  lang: GeoSearchQuerySchema.shape.lang,
})
export type GeoBoundaryQuery = z.input<typeof GeoBoundaryQuerySchema>

export const GeoBoundaryResponseSchema = z.object({
  area: PlaceAreaSchema,
  /** Vertices after simplification. */
  points: z.number().int().min(0),
  attribution: z.string(),
  cached: z.boolean(),
})
export type GeoBoundaryResponse = z.infer<typeof GeoBoundaryResponseSchema>
